import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ClassStudentSelect } from "@/components/ClassStudentSelect";
import { GRADE_GROUPS } from "@/lib/subjects";
import { Mountain, Lock, Unlock, CheckCircle2, Plus, Trash2, Settings2, BookOpen, Trophy, X, Save } from "lucide-react";

const TSTATUS = {
  baslanmadi: { label: "Başlanmadı", chip: "bg-slate-100 text-slate-500 border-slate-200", dot: "bg-slate-300" },
  calisiliyor: { label: "Çalışılıyor", chip: "bg-amber-100 text-amber-700 border-amber-300", dot: "bg-amber-500" },
  bitti: { label: "Bitti ✓", chip: "bg-emerald-100 text-emerald-700 border-emerald-300", dot: "bg-emerald-500" },
};
const TORDER = ["baslanmadi", "calisiliyor", "bitti"];

export default function MatematikKampi() {
  const { user } = useAuth();
  const isEditor = user?.role === "admin" || user?.role === "koc";
  const [params] = useSearchParams();
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState(params.get("student") || null);
  const [grade, setGrade] = useState(Number(params.get("grade")) || 5);
  const [kurs, setKurs] = useState([]);
  const [prog, setProg] = useState({ topics: {}, resources: {}, unlocked: { k0: true } });
  const [editStruct, setEditStruct] = useState(false);
  const [resForm, setResForm] = useState(null); // {kurId, resId}

  useEffect(() => {
    if (isEditor) api.get("/students").then(({ data }) => setStudents(data));
    else api.get("/my/context").then(({ data }) => { setStudentId(data.student_id); });
  }, [isEditor]);

  const activeGrade = useMemo(() => students.find((s) => s.id === studentId)?.grade ?? grade, [students, studentId, grade]);

  const loadStructure = (g) => api.get("/math-camp/structure", { params: { grade: g } }).then(({ data }) => setKurs(data.kurs || []));
  const loadProgress = () => { if (studentId) api.get("/math-camp/progress", { params: { student_id: studentId } }).then(({ data }) => setProg({ topics: {}, resources: {}, unlocked: { k0: true }, ...data })); };

  useEffect(() => { if (activeGrade) loadStructure(activeGrade); }, [activeGrade]);
  useEffect(() => { loadProgress(); }, [studentId]);

  const tkey = (kurId, topic) => `${kurId}|||${topic}`;
  const topicStatus = (kurId, topic) => prog.topics?.[tkey(kurId, topic)] || "baslanmadi";
  const resStat = (kurId, resId) => prog.resources?.[`${kurId}|||${resId}`] || {};
  const isUnlocked = (i, kurId) => i === 0 || prog.unlocked?.[kurId];

  const completedTopics = (kur) => (kur.topics || []).filter((t) => topicStatus(kur.id, t) === "bitti").length;
  const kurDone = (kur) => completedTopics(kur) >= (kur.hedef || kur.topics?.length || 0);

  const setTopic = async (kurId, topic, status) => {
    try {
      await api.post("/math-camp/topic", { student_id: studentId, kur_id: kurId, topic, status });
      setProg((p) => ({ ...p, topics: { ...p.topics, [tkey(kurId, topic)]: status } }));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const unlockKur = async (kurId, val) => {
    try {
      await api.post("/math-camp/unlock", { student_id: studentId, kur_id: kurId, unlocked: val });
      setProg((p) => ({ ...p, unlocked: { ...p.unlocked, [kurId]: val } }));
      toast.success(val ? "Kur açıldı ✓" : "Kur kilitlendi");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveRes = async () => {
    const r = resForm;
    try {
      await api.post("/math-camp/resource", { student_id: studentId, kur_id: r.kurId, res_id: r.resId, name: r.name, soru: Number(r.soru) || 0, dogru: Number(r.dogru) || 0, yanlis: Number(r.yanlis) || 0, bos: Number(r.bos) || 0, odev: r.odev });
      toast.success("Kaynak kaydedildi ✓");
      setResForm(null);
      loadProgress();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // ---- Structure editing (admin/koç) ----
  const saveStructure = async (next) => {
    setKurs(next);
    try { await api.put("/math-camp/structure", { grade: activeGrade, kurs: next }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addKur = () => saveStructure([...kurs, { id: `k${Date.now()}`, name: `${kurs.length + 1}. Kur`, topics: [], resources: [], hedef: 0 }]);
  const delKur = (i) => { if (window.confirm("Kur silinsin mi?")) saveStructure(kurs.filter((_, j) => j !== i)); };
  const patchKur = (i, patch) => saveStructure(kurs.map((k, j) => (j === i ? { ...k, ...patch } : k)));
  const addTopic = (i, t) => { if (t.trim()) patchKur(i, { topics: [...(kurs[i].topics || []), t.trim()] }); };
  const delTopic = (i, ti) => patchKur(i, { topics: kurs[i].topics.filter((_, j) => j !== ti) });
  const addResource = (i, yayin, name) => { if (name.trim()) patchKur(i, { resources: [...(kurs[i].resources || []), { id: `r${Date.now()}`, name: name.trim(), yayin: yayin.trim() }] }); };
  const delResource = (i, ri) => patchKur(i, { resources: kurs[i].resources.filter((_, j) => j !== ri) });

  const inp = "rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm outline-none focus:border-indigo-400";

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-indigo-100 text-indigo-600"><Mountain className="h-6 w-6" /></div>
          <div>
            <h1 className="font-display text-3xl font-extrabold text-slate-900">Matematik Kampı</h1>
            <p className="text-slate-500">Kur kur ilerle — her kurun sonunda sınav, biten konular onaylanınca bir sonraki kur açılır.</p>
          </div>
        </div>
        {isEditor && (
          <button data-testid="mk-edit-struct-btn" onClick={() => setEditStruct((o) => !o)} className={`flex items-center gap-2 rounded-xl px-4 py-2.5 font-bold transition active:scale-95 ${editStruct ? "bg-indigo-600 text-white" : "border border-slate-200 bg-white text-slate-700 hover:shadow-md"}`}>
            <Settings2 className="h-5 w-5" /> {editStruct ? "Düzenlemeyi Bitir" : "Kur / İçerik Düzenle"}
          </button>
        )}
      </div>

      {isEditor && (
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          {editStruct ? (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-sm font-bold text-slate-600">Düzenlenecek Sınıf:</span>
              <select data-testid="mk-grade-select" value={activeGrade || grade} onChange={(e) => { setGrade(Number(e.target.value)); setStudentId(null); }} className={inp}>
                {GRADE_GROUPS.map((g) => <optgroup key={g.label} label={g.label}>{g.grades.map((gr) => <option key={gr} value={gr}>{gr}. Sınıf</option>)}</optgroup>)}
              </select>
              <span className="text-xs text-slate-400">Yapı tüm {grade}. sınıf öğrencileri için ortaktır.</span>
            </div>
          ) : (
            <>
              <label className="mb-1 block text-sm font-semibold text-slate-700">Öğrenci Seç</label>
              <ClassStudentSelect students={students} value={studentId} onChange={(v) => setStudentId(v || null)} testid="mk-student-select" placeholder="İlerlemesini görmek için öğrenci seçin…" />
            </>
          )}
        </div>
      )}

      {!editStruct && !studentId ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-12 text-center text-slate-500">{isEditor ? "İlerleme için bir öğrenci seçin." : "Bağlı öğrenci bulunamadı."}</div>
      ) : (
        <div className="space-y-4">
          {kurs.map((kur, i) => {
            const unlocked = editStruct || isUnlocked(i, kur.id);
            const done = kurDone(kur);
            return (
              <div key={kur.id} data-testid={`mk-kur-${i}`} className={`overflow-hidden rounded-2xl border shadow-sm ${unlocked ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50 opacity-70"}`}>
                <div className={`flex flex-wrap items-center justify-between gap-2 border-b px-5 py-4 ${done ? "bg-emerald-50 border-emerald-100" : "bg-slate-50/60 border-slate-100"}`}>
                  <div className="flex items-center gap-2">
                    {unlocked ? <Unlock className="h-5 w-5 text-emerald-500" /> : <Lock className="h-5 w-5 text-slate-400" />}
                    {editStruct ? (
                      <input value={kur.name} onChange={(e) => patchKur(i, { name: e.target.value })} className={`${inp} font-bold`} data-testid={`mk-kur-name-${i}`} />
                    ) : (
                      <h3 className="font-display text-lg font-bold text-slate-900">{kur.name}</h3>
                    )}
                    {done && !editStruct && <span className="rounded-full bg-emerald-600 px-2.5 py-0.5 text-xs font-bold text-white">Tamamlandı</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    {!editStruct && <span className="text-xs font-bold text-slate-500">{completedTopics(kur)}/{kur.hedef || kur.topics?.length || 0} konu · hedef {kur.hedef || kur.topics?.length || 0}</span>}
                    {editStruct && <>
                      <label className="text-xs font-semibold text-slate-500">Hedef:</label>
                      <input type="number" min={0} value={kur.hedef ?? ""} onChange={(e) => patchKur(i, { hedef: Number(e.target.value) || 0 })} className={`${inp} w-16`} data-testid={`mk-kur-hedef-${i}`} />
                      <button onClick={() => delKur(i)} className="rounded-lg bg-rose-50 p-2 text-rose-500 hover:bg-rose-100" data-testid={`mk-del-kur-${i}`}><Trash2 className="h-4 w-4" /></button>
                    </>}
                  </div>
                </div>

                {unlocked && (
                  <div className="space-y-4 p-5">
                    {/* Konular */}
                    <div>
                      <h4 className="mb-2 flex items-center gap-1.5 text-sm font-bold text-slate-600"><BookOpen className="h-4 w-4" /> Konular</h4>
                      <div className="flex flex-wrap gap-2">
                        {(kur.topics || []).map((t, ti) => {
                          const st = topicStatus(kur.id, t);
                          return (
                            <div key={ti} data-testid={`mk-topic-${i}-${ti}`} className={`flex items-center gap-1.5 rounded-xl border px-3 py-1.5 text-sm font-semibold ${TSTATUS[st].chip}`}>
                              <span className={`h-2 w-2 rounded-full ${TSTATUS[st].dot}`} /> {t}
                              {editStruct ? (
                                <button onClick={() => delTopic(i, ti)} className="ml-1 text-slate-400 hover:text-rose-600"><X className="h-3.5 w-3.5" /></button>
                              ) : isEditor && (
                                <select value={st} onChange={(e) => setTopic(kur.id, t, e.target.value)} className="ml-1 rounded border border-white/50 bg-white/60 text-[11px] font-bold outline-none" data-testid={`mk-topic-status-${i}-${ti}`}>
                                  {TORDER.map((s) => <option key={s} value={s}>{TSTATUS[s].label}</option>)}
                                </select>
                              )}
                            </div>
                          );
                        })}
                        {editStruct && <TopicAdder onAdd={(t) => addTopic(i, t)} testid={`mk-add-topic-${i}`} />}
                      </div>
                    </div>

                    {/* Kaynaklar */}
                    <div>
                      <h4 className="mb-2 flex items-center gap-1.5 text-sm font-bold text-slate-600"><Trophy className="h-4 w-4" /> Kaynaklar</h4>
                      {!editStruct && (() => {
                        const t = (kur.resources || []).reduce((a, r) => { const s = resStat(kur.id, r.id); return { soru: a.soru + (s.soru || 0), dogru: a.dogru + (s.dogru || 0), yanlis: a.yanlis + (s.yanlis || 0), bos: a.bos + (s.bos || 0) }; }, { soru: 0, dogru: 0, yanlis: 0, bos: 0 });
                        return t.soru > 0 ? <div data-testid={`mk-kur-totals-${i}`} className="mb-2 rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-bold text-slate-600">Kur Toplamı — Soru: {t.soru} · <span className="text-emerald-600">D: {t.dogru}</span> · <span className="text-rose-600">Y: {t.yanlis}</span> · B: {t.bos} · Net {(t.dogru - t.yanlis / 3).toFixed(2)}</div> : null;
                      })()}
                      <div className="space-y-2">
                        {(kur.resources || []).map((r, ri) => {
                          const stat = resStat(kur.id, r.id);
                          return (
                            <div key={r.id} data-testid={`mk-res-${i}-${ri}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2">
                              <div className="flex items-center gap-2">
                                {r.yayin && <span className="rounded-md bg-slate-200 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-500">{r.yayin}</span>}
                                <span className="font-semibold text-slate-700">{r.name}</span>
                                {stat.odev && <span className="rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-bold text-violet-700">Ödev: {stat.odev}</span>}
                                {(stat.soru > 0) && <span className="text-xs text-slate-500">S:{stat.soru} · <b className="text-emerald-600">D:{stat.dogru}</b> · <b className="text-rose-600">Y:{stat.yanlis}</b> · B:{stat.bos} · Net {stat.net?.toFixed(2)}</span>}
                              </div>
                              <div className="flex items-center gap-1">
                                {editStruct ? (
                                  <button onClick={() => delResource(i, ri)} className="rounded-lg p-1.5 text-rose-400 hover:bg-rose-50"><Trash2 className="h-4 w-4" /></button>
                                ) : isEditor && (
                                  <button data-testid={`mk-res-edit-${i}-${ri}`} onClick={() => setResForm({ kurId: kur.id, resId: r.id, name: r.name, yayin: r.yayin || "", soru: stat.soru || "", dogru: stat.dogru || "", yanlis: stat.yanlis || "", bos: stat.bos || "", odev: stat.odev || "" })} className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white transition hover:bg-indigo-700 active:scale-95">Ödev / Sonuç Gir</button>
                                )}
                              </div>
                            </div>
                          );
                        })}
                        {editStruct && <ResourceAdder onAdd={(y, n) => addResource(i, y, n)} testid={`mk-add-res-${i}`} />}
                        {!editStruct && (kur.resources || []).length === 0 && <p className="py-2 text-center text-xs text-slate-400">Kaynak tanımlı değil.</p>}
                      </div>
                    </div>

                    {/* Kur Sonu Sınavı */}
                    {!editStruct && (() => { const ex = resStat(kur.id, "kur_sinavi"); return (
                      <div data-testid={`mk-exam-${i}`} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50/60 px-4 py-3">
                        <div className="flex items-center gap-2">
                          <Trophy className="h-4 w-4 text-amber-600" />
                          <span className="text-sm font-bold text-amber-800">Kur Sonu Sınavı</span>
                          {ex.soru > 0 && <span className="text-xs text-slate-600">S:{ex.soru} · <b className="text-emerald-600">D:{ex.dogru}</b> · <b className="text-rose-600">Y:{ex.yanlis}</b> · B:{ex.bos} · Net {ex.net?.toFixed(2)}</span>}
                        </div>
                        {isEditor && <button data-testid={`mk-exam-edit-${i}`} onClick={() => setResForm({ kurId: kur.id, resId: "kur_sinavi", name: "Kur Sonu Sınavı", yayin: "", soru: ex.soru || "", dogru: ex.dogru || "", yanlis: ex.yanlis || "", bos: ex.bos || "", odev: ex.odev || "" })} className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-white transition hover:bg-amber-700 active:scale-95">Sonuç Gir</button>}
                      </div>
                    ); })()}

                    {/* Kur sonu sınav + sonraki kur */}
                    {!editStruct && isEditor && (
                      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-indigo-100 bg-indigo-50/50 px-4 py-3">
                        <span className="text-sm font-semibold text-indigo-700">{done ? "✓ Hedef tamamlandı — bir sonraki kuru açabilirsiniz." : `İlerleme: ${completedTopics(kur)}/${kur.hedef || kur.topics?.length || 0} — hedef dolmadan da admin/koç olarak kuru açabilirsiniz.`}</span>
                        {i < kurs.length - 1 && (
                          <button
                            data-testid={`mk-unlock-next-${i}`}
                            onClick={() => unlockKur(kurs[i + 1].id, true)}
                            className="flex items-center gap-1.5 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-emerald-700 active:scale-95">
                            <CheckCircle2 className="h-4 w-4" /> Sonraki Kuru Aç (Onayla)
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          {editStruct && (
            <button data-testid="mk-add-kur-btn" onClick={addKur} className="flex w-full items-center justify-center gap-2 rounded-2xl border border-dashed border-slate-300 bg-white py-4 font-bold text-slate-500 transition hover:border-indigo-400 hover:text-indigo-600 active:scale-[0.99]">
              <Plus className="h-5 w-5" /> Kur Ekle
            </button>
          )}
        </div>
      )}

      {/* Kaynak ödev/sonuç diyaloğu */}
      {resForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setResForm(null)}>
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-4 font-display text-xl font-bold text-slate-900">{resForm.yayin ? `${resForm.yayin} — ` : ""}{resForm.name}</h3>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-bold uppercase text-slate-500">Ödev</label>
                <input data-testid="mk-res-odev" value={resForm.odev} onChange={(e) => setResForm((f) => ({ ...f, odev: e.target.value }))} placeholder="örn. 1-20 arası sorular" className={`${inp} w-full`} />
              </div>
              <div className="grid grid-cols-4 gap-2">
                {["soru", "dogru", "yanlis", "bos"].map((k) => (
                  <div key={k}>
                    <label className="mb-1 block text-xs font-bold uppercase text-slate-500">{k === "soru" ? "Soru" : k === "dogru" ? "Doğru" : k === "yanlis" ? "Yanlış" : "Boş"}</label>
                    <input data-testid={`mk-res-${k}`} type="number" min={0} value={resForm[k]} onChange={(e) => setResForm((f) => ({ ...f, [k]: e.target.value }))} className={`${inp} w-full`} />
                  </div>
                ))}
              </div>
            </div>
            <div className="mt-5 flex gap-2">
              <button onClick={() => setResForm(null)} className="flex-1 rounded-xl border border-slate-200 py-2.5 font-bold text-slate-600 transition hover:bg-slate-50">Vazgeç</button>
              <button data-testid="mk-res-save" onClick={saveRes} className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-indigo-600 py-2.5 font-bold text-white transition hover:bg-indigo-700 active:scale-95"><Save className="h-4 w-4" /> Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TopicAdder({ onAdd, testid }) {
  const [v, setV] = useState("");
  return (
    <div className="flex items-center gap-1">
      <input data-testid={testid} value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { onAdd(v); setV(""); } }} placeholder="+ konu" className="w-32 rounded-lg border border-dashed border-slate-300 px-2 py-1.5 text-sm outline-none focus:border-indigo-400" />
    </div>
  );
}

function ResourceAdder({ onAdd, testid }) {
  const [yayin, setYayin] = useState("");
  const [v, setV] = useState("");
  const go = () => { onAdd(yayin, v); setV(""); setYayin(""); };
  return (
    <div className="flex items-center gap-2">
      <input data-testid={`${testid}-yayin`} value={yayin} onChange={(e) => setYayin(e.target.value)} placeholder="Yayın (örn. Hız)" className="w-32 rounded-lg border border-dashed border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-indigo-400" />
      <input data-testid={testid} value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") go(); }} placeholder="Kitap adı…" className="flex-1 rounded-lg border border-dashed border-slate-300 px-3 py-1.5 text-sm outline-none focus:border-indigo-400" />
      <button data-testid={`${testid}-btn`} onClick={go} className="flex items-center gap-1 rounded-lg bg-slate-700 px-3 py-1.5 text-sm font-bold text-white transition hover:bg-slate-800 active:scale-95"><Plus className="h-4 w-4" /> Kaynak</button>
    </div>
  );
}
